import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  USER_INTERFACE_ARROW_04_RIGHT_THICK,
  USER_INTERFACE_ARROW_04_LEFT_THICK,
} from '@bwoty-web/styleguide';
import DesktopArrowButton from './desktopArrowButton';
import {
  getHotelSubNavScrollDistance,
  setHotelSubNavScrollDistance,
} from '../../../utils/scroll';
import './horizontalScroll.scss';

const HorizontalScroll = forwardRef(
  ({ children, stepSize, initialScroll, arrowGutters }, ref) => {
    const [scrollable, setScrollable] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(initialScroll);
    const [scrollEnd, setScrollEnd] = useState(false);
    const scrl = useRef(null);

    useImperativeHandle(ref, () => ({
      updateScrollPosition() {
        if (scrollPosition !== initialScroll) setScrollPosition(initialScroll);
        else setHotelSubNavScrollDistance(getHotelSubNavScrollDistance());
      },
    }));

    useEffect(() => {
      const el = scrl.current;
      setScrollable(el.offsetWidth < el.scrollWidth);
    }, [children]);

    useEffect(() => {
      setScrollPosition(initialScroll);
    }, [initialScroll]);

    useEffect(() => {
      const el = scrl.current;
      el.scrollLeft = scrollPosition;
    }, [scrollPosition]);

    const slide = (shift) => {
      const el = scrl.current;
      const hasOverScrolled =
        scrollPosition + el.offsetWidth + shift > el.scrollWidth;
      const hasUnderScrolled = scrollPosition + shift < 0;

      if (hasOverScrolled) {
        setScrollPosition(el.scrollWidth - el.offsetWidth);
      } else if (hasUnderScrolled) {
        setScrollPosition(0);
      } else {
        setScrollPosition(scrollPosition + shift);
      }
    };

    const scrollHandler = ({ target: e }) => {
      if (e.scrollLeft + e.offsetWidth >= e.scrollWidth - 50)
        setScrollEnd(true);
      else if (scrollEnd === true) setScrollEnd(false);
    };

    return (
      <div
        className={cx('gw-horizontal-scroll', {
          'gw-horizontal-scroll-end': scrollEnd,
        })}
      >
        {scrollable && scrollPosition > 0 && (
          <DesktopArrowButton
            path={USER_INTERFACE_ARROW_04_LEFT_THICK}
            onClick={() => slide(-stepSize)}
            arrowGutters={arrowGutters}
          />
        )}
        <div
          className="gw-horizontal-scroll__container"
          onScroll={(e) => scrollHandler(e)}
          id="gw-horizontal-scroll"
          ref={scrl}
        >
          {children}
        </div>
        {scrollable && !scrollEnd && (
          <DesktopArrowButton
            path={USER_INTERFACE_ARROW_04_RIGHT_THICK}
            onClick={() => slide(+stepSize)}
            right
            arrowGutters={arrowGutters}
          />
        )}
      </div>
    );
  },
);

HorizontalScroll.displayName = 'HorizontalScroll';

HorizontalScroll.propTypes = {
  children: PropTypes.node.isRequired,
  stepSize: PropTypes.number.isRequired,
  initialScroll: PropTypes.number,
  arrowGutters: PropTypes.bool,
};

HorizontalScroll.defaultProps = {
  initialScroll: 0,
  arrowGutters: false,
};

export default HorizontalScroll;
